// The Queuing schematic as a graph: service groups → segmentation → queues → work profiles.
import type { OperationDoc } from './types'

export type Column = 'group' | 'segmentation' | 'queue' | 'work'
// 'g:<id>' | 's:<groupId>' (segmentation card) | 'r:<segmentId>' | 'd:<groupId>' (default row) | 'q:<id>' | 'w:<id>'
export type NodeId = string
export interface Edge { id: string; from: NodeId; to: NodeId; kind: 'route' | 'default' | 'calls' | 'feeds' }
// rows: 's:<g>' → its 'r:' rows in segment order, then 'd:<g>'
export interface Graph { columns: Record<Column, NodeId[]>; rows: Record<string, NodeId[]>; edges: Edge[] }

const edge = (from: NodeId, to: NodeId, kind: Edge['kind']): Edge => ({ id: `${from}>${to}`, from, to, kind })
const mean = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length

export function buildGraph(doc: OperationDoc): Graph {
  const queueIds = new Set(doc.queues.map((q) => q.id))
  const rows: Record<string, NodeId[]> = {}
  const edges: Edge[] = []
  const seen = new Set<string>()
  const add = (e: Edge) => { if (!seen.has(e.id)) { seen.add(e.id); edges.push(e) } }

  // Rows in reading order; a queue's rank is the mean index of the rows that reach it.
  const reach = new Map<string, number[]>()
  let rowIndex = 0
  const route = (row: NodeId, queueId: string, kind: 'route' | 'default') => {
    const i = rowIndex++
    if (!queueIds.has(queueId)) return
    add(edge(row, `q:${queueId}`, kind))
    reach.set(queueId, [...(reach.get(queueId) ?? []), i])
  }
  for (const g of doc.groups) {
    const card = `s:${g.id}`
    add(edge(`g:${g.id}`, card, 'feeds'))
    rows[card] = [...g.segments.map((s) => `r:${s.id}`), `d:${g.id}`]
    for (const s of g.segments) route(`r:${s.id}`, s.queueId, 'route')
    route(`d:${g.id}`, g.defaultQueueId, 'default')
  }

  const queues = [...doc.queues].sort((a, b) => {
    const ra = reach.get(a.id), rb = reach.get(b.id)
    if (ra && !rb) return -1
    if (rb && !ra) return 1
    if (ra && rb) { const d = mean(ra) - mean(rb); if (d !== 0) return d }
    return a.sortOrder - b.sortOrder || a.code.localeCompare(b.code)
  })
  const queueRank = new Map(queues.map((q, i) => [q.id, i]))

  const work = doc.workProfiles.map((w) => {
    const ranks = [...new Set(w.queueIds)].flatMap((id) => (queueRank.has(id) ? [queueRank.get(id)!] : []))
    return { w, rank: ranks.length ? mean(ranks) : Infinity }
  }).sort((a, b) => (a.rank === b.rank ? 0 : a.rank - b.rank) || a.w.name.localeCompare(b.w.name))
  for (const { w } of work) {
    for (const id of w.queueIds) if (queueIds.has(id)) add(edge(`q:${id}`, `w:${w.id}`, 'calls'))
  }

  return {
    columns: {
      group: doc.groups.map((g) => `g:${g.id}`),
      segmentation: doc.groups.map((g) => `s:${g.id}`),
      queue: queues.map((q) => `q:${q.id}`),
      work: work.map(({ w }) => `w:${w.id}`),
    },
    rows,
    edges,
  }
}

// A row sits inside its segmentation card: upstream of the row is the card, downstream of the card its rows.
function containerOf(graph: Graph, node: NodeId): NodeId | undefined {
  if (node.startsWith('d:')) return `s:${node.slice(2)}`
  if (!node.startsWith('r:')) return undefined
  for (const [card, rows] of Object.entries(graph.rows)) if (rows.includes(node)) return card
  return undefined
}

/** Everything the node draws from and everything it feeds, never its siblings. */
export function highlight(graph: Graph, node: NodeId): { nodes: Set<NodeId>; edges: Set<string> } {
  const nodes = new Set<NodeId>([node])
  const lit = new Set<string>()
  const walk = (start: NodeId, down: boolean) => {
    const stack = [start]
    while (stack.length) {
      const n = stack.pop()!
      const next: NodeId[] = []
      for (const e of graph.edges) {
        if (down ? e.from === n : e.to === n) { lit.add(e.id); next.push(down ? e.to : e.from) }
      }
      if (down) next.push(...(graph.rows[n] ?? []))
      else { const c = containerOf(graph, n); if (c) next.push(c) }
      for (const m of next) if (!nodes.has(m)) { nodes.add(m); stack.push(m) }
    }
  }
  walk(node, false)
  walk(node, true)
  return { nodes, edges: lit }
}

const num = (n: number) => String(Math.round(n * 10) / 10)

/** A horizontal S-curve from the right edge of one card to the left edge of the next. */
export function connectorPath(from: { x: number; y: number }, to: { x: number; y: number }): string {
  const mid = num((from.x + to.x) / 2)
  return `M${num(from.x)} ${num(from.y)} C${mid} ${num(from.y)} ${mid} ${num(to.y)} ${num(to.x)} ${num(to.y)}`
}
